// Import Depndencies
import { isRouteErrorResponse, useRouteError, Link } from "react-router";
import { lazy } from "react";
import { Sentry } from "utils/sentry.init";

// Local Imports
import { Loadable } from "components/shared/Loadable";
import { Page } from "components/shared/Page";

// ----------------------------------------------------------------------

const app = {
  401: lazy(() => import("./401")),
  404: lazy(() => import("./404")),
  429: lazy(() => import("./429")),
  500: lazy(() => import("./500")),
};

function RootErrorBoundary() {
  const error = useRouteError();

  if (isRouteErrorResponse(error)) {
    // Report non-404 route errors to Sentry
    // 404s are excluded as they're typically user navigation errors, not bugs
    if (error.status !== 404) {
      Sentry.captureException(error, {
        tags: {
          errorType: "route_error_response",
          statusCode: error.status,
        },
        extra: {
          status: error.status,
          statusText: error.statusText,
          data: error.data,
        },
      });
    }
    const Component = Loadable(app[error.status]);
    return <Component />;
  }

  // Log full error details for debugging
  console.error("Unhandled route error:", error);

  // Report unhandled errors to Sentry with full context
  Sentry.captureException(error, {
    tags: { errorType: "unhandled_route_error" },
    extra: {
      message: error?.message,
      stack: error?.stack,
    },
  });

  const message = error?.message || error?.data?.message || "An unexpected error occurred.";
  const statusText = error?.statusText || error?.data?.statusText;
  const statusCode = error?.status || error?.data?.statusCode;

  return (
    <Page title="Error">
      <main className="min-h-100vh relative grid w-full grow grid-cols-1 place-items-center p-4">
        <div className="w-full max-w-[28rem] text-center bg-white/5 dark:bg-dark-800 rounded-lg border border-white/10 p-6">
          <p className="text-xl font-semibold text-gray-800 dark:text-dark-50">Something went wrong</p>
          <p className="mt-2 text-sm text-gray-600 dark:text-dark-200 break-words">{message}</p>
          {(statusCode || statusText) && (
            <p className="mt-1 text-xs text-gray-500 dark:text-dark-300">
              {statusCode ? `Status: ${statusCode}` : null}
              {statusCode && statusText ? " · " : null}
              {statusText || null}
            </p>
          )}
          {!import.meta.env.PROD && error?.stack && (
            <pre className="mt-3 max-h-60 overflow-auto whitespace-pre-wrap text-left rounded bg-black/40 p-3 text-xs text-gray-200">{String(error.stack)}</pre>
          )}
          <div className="mt-4">
            <Link to="/" className="text-primary-500 hover:text-primary-400 underline text-sm">Go back home</Link>
          </div>
        </div>
      </main>
    </Page>
  );
}

export default RootErrorBoundary;

